import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Sparkles } from 'lucide-react';
import { Badge, Button, EmptyRow, SkeletonRows, relativeTime } from '../kit';
import ReflectDialog from './ReflectDialog';
import ThreadLink from './ThreadLink';
import { REFLECTION_TRIGGER_LABEL, errorMessage, exactDate, plural, signalsText } from './skillsLogic';
import { listAiSkillReflections } from './skillsApi';

const PAGE_SIZE = 50;

/**
 * Reflections tab: the audit log of every reflection run (including "no
 * skill needed"), newest first, paged with the API's `before` cursor.
 */
export default function ReflectionsTab({ onOpenSkill, onSkillsChanged }) {
  const [rows, setRows] = useState([]);
  const [viewerId, setViewerId] = useState(null);
  const [nextBefore, setNextBefore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [reflectOpen, setReflectOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listAiSkillReflections({ limit: PAGE_SIZE });
      setRows(Array.isArray(res?.reflections) ? res.reflections : []);
      setNextBefore(res?.nextBefore || null);
      setViewerId(res?.viewerId || null);
    } catch (err) {
      setError(errorMessage(err, 'Failed to load reflections'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    if (!nextBefore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const res = await listAiSkillReflections({ limit: PAGE_SIZE, before: nextBefore });
      const more = Array.isArray(res?.reflections) ? res.reflections : [];
      setRows((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...more.filter((r) => !seen.has(r.id))];
      });
      setNextBefore(res?.nextBefore || null);
    } catch (err) {
      setError(errorMessage(err, 'Failed to load more reflections'));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="ak-frame">
      <div className="ak-toolbar">
        <Button icon={Sparkles} onClick={() => setReflectOpen(true)}>Reflect now</Button>
        <Button icon={RefreshCw} onClick={load} disabled={loading}>Refresh</Button>
        <span className="ak-toolbar-meta">
          {loading ? 'Loading…' : `${plural(rows.length, 'reflection')} · newest first`}
        </span>
      </div>
      {error ? <div className="ak-error" role="alert">{error}<Button size="small" onClick={rows.length ? loadMore : load}>Try again</Button></div> : null}
      <div className="ak-scroll">
        <table className="ak-tbl sk-refl-tbl">
          <thead>
            <tr>
              <th>When</th>
              <th>Trigger</th>
              <th className="ak-right">Turns</th>
              <th>Signals</th>
              <th>Decision</th>
              <th>Reason</th>
              <th>Thread</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows rows={8} columns={7} />
            ) : rows.length === 0 ? (
              <EmptyRow
                colSpan={7}
                title={error ? 'Reflections unavailable' : 'No reflections yet'}
                body={error ? 'Fix the error above and refresh.' : 'The assistant reflects after conversations of three or more turns, and after a downvote. Use Reflect now to run one on a thread.'}
              />
            ) : (
              rows.map((r) => (
                <tr key={r.id}>
                  <td className="sk-nowrap" title={exactDate(r.createdAt)}>{relativeTime(r.createdAt) || '—'}</td>
                  <td className="sk-nowrap">{REFLECTION_TRIGGER_LABEL[r.trigger] || r.trigger || '—'}</td>
                  <td className="ak-right ak-mono">{r.turnCount ?? '—'}</td>
                  <td className="sk-nowrap sk-signals">{signalsText(r.signals) || '—'}</td>
                  <td className="sk-nowrap">
                    {r.decision === 'propose' ? (
                      <span className="sk-decision">
                        <Badge tone="warn">Proposed</Badge>
                        {(r.skillIds || []).map((id, i) => (
                          <button key={id} type="button" className="ak-link" onClick={() => onOpenSkill(id)} title="Open in Review">
                            {r.skillIds.length > 1 ? `skill ${i + 1}` : 'view'}
                          </button>
                        ))}
                      </span>
                    ) : (
                      <Badge>No skill needed</Badge>
                    )}
                  </td>
                  <td className="sk-reason">{r.reason || '—'}</td>
                  <td className="sk-nowrap"><ThreadLink threadId={r.threadId} ownerId={r.userId} viewerId={viewerId} /></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!loading && rows.length ? (
        <div className="ak-pager">
          <span>{nextBefore ? `Showing the latest ${rows.length}` : `All ${plural(rows.length, 'reflection')}`}</span>
          {nextBefore ? (
            <Button size="small" onClick={loadMore} disabled={loadingMore} style={{ marginLeft: 'auto' }}>
              {loadingMore ? 'Loading…' : `Load ${PAGE_SIZE} more`}
            </Button>
          ) : null}
        </div>
      ) : null}
      <ReflectDialog
        open={reflectOpen}
        onClose={() => setReflectOpen(false)}
        onOpenSkill={onOpenSkill}
        onDone={(r) => {
          if (r?.ran) load();
          if (r?.createdIds?.length || r?.updatedIds?.length) onSkillsChanged && onSkillsChanged();
        }}
      />
    </div>
  );
}
